import { Box, Text, Grid, Input, FormControl, FormLabel, Button, FormErrorMessage, Checkbox, GridItem, Select } from "@chakra-ui/react";
import MyDiv from "./salaryCompensation.style";
import { useState, useEffect } from "react";
import { useFetch } from "../../../../../hooks/useFetch";
import { AlertCard, CustomToast } from "../../../../../components";
import { useForm, Controller, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { calculateEmployeeSalary, calculateInternSalary } from "../../../../../utilities/salaryCalculator";
import { HrlenseInput } from "../../../../../components/Input/Input";
export type SalaryForm = {
    salary_Per_Month: number;
    gross_Salary_per_anum?: number;
    basic_salary?: number;
    hra?: number;
    conveyance?: number;
    total_Deduction?: number;
    net_Salary?: number;
    mealAllowance?: boolean;
    transportAllowance?: boolean;
    healthInsurance?: boolean;
    basic_Salary_for_pf?: number;
    employeePF?: number;
    employerPF?: number;
    esicEmployee?: number;
    esicEmployer?: number;
    monthlyCTC?: number;
    annualCTC?: number;
    netTakeHomeSalary?: number;
    esicApplicable?: boolean;
    includePf?: boolean;
    includeEsic?: boolean;
    employement_Type: "EMPLOYEE" | "INTERN";
    intern_Payment_Type?: "PAID" | "UNPAID" | "";
};

interface SalaryCompensationProps {
    nextStep: () => void;
    prevStep: () => void;
    salary: SalaryForm;
    setSalary: React.Dispatch<React.SetStateAction<SalaryForm>>;
    employeeId: number | null;
    branchId: number;
    departmentId: number;
}

export const salaryCompensationSchema =
    yup.object({
        employement_Type: yup.string().oneOf(["EMPLOYEE", "INTERN"]).required("Employment type is required"),

        intern_Payment_Type: yup.string().when("employement_Type", {
            is: (val: string) => val === "INTERN",
            then: (schema) => schema.required("Intern payment type is required"),
            otherwise: (schema) =>
                schema.strip(),
        }),

        salary_Per_Month: yup.number().when(["employement_Type", "intern_Payment_Type"], {
            is: (type: string, internType: string) => type === "EMPLOYEE" || internType === "PAID",
            then: (schema) => schema.typeError("Salary must be a number").required("Salary is required"),
            otherwise: (schema) => schema.strip(),
        }
        ),

    });



const SalaryCompensation: React.FC<SalaryCompensationProps> = ({ nextStep, prevStep, salary, setSalary, employeeId, branchId, departmentId }) => {
    const [loading, setLoading] = useState(false);
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [salaryRule, setSalaryRule] = useState<any>(null);
    const [benfits, setBenfits] = useState<any | null>(null);
    const [calculationRuleWithoutPF, setCalculationRuleWithoutPF] = useState<any | null>(null)
    const [alertConfig, setAlertConfig] = useState<{ description: string; colorScheme: string; onConfirm: () => void; } | null>(null);
    const [includePF, setIncludePF] = useState(true);
    const [includeESIC, setIncludeESIC] = useState(true);


    const { control, handleSubmit, formState: { errors }, watch, reset, setValue } = useForm<SalaryForm>({
        resolver: yupResolver(salaryCompensationSchema) as Resolver<SalaryForm>,
        defaultValues: salary,
    });


    const getPercentage = (value?: number) => {
        const gross = Number(watch("salary_Per_Month"));

        if (!gross || !value) return "";

        const percentage = ((Number(value) / gross) * 100).toFixed(2);

        return `(${parseFloat(percentage)}%)`;
    };


    const grossMonthValue = parseFloat(watch("salary_Per_Month")?.toString() || "0");
    const mealAllowance = watch("mealAllowance");
    const transportAllowance = watch("transportAllowance");
    const healthInsurance = watch("healthInsurance");

    const employement_Type = watch("employement_Type");
    const intern_Payment_Type = watch("intern_Payment_Type");

    const isIntern = employement_Type === "INTERN";


    const loadSalaryRule = async () => {
        if (isIntern && !intern_Payment_Type) {
            setSalaryRule(null);
            return;
        }

        const params = new URLSearchParams({
            branchId: String(branchId),
            departmentId: String(departmentId),
            employementType: employement_Type ?? "EMPLOYEE",
            internPaymentType: employement_Type === "INTERN" ? intern_Payment_Type ?? "" : "",
        });
        const response = await fetchApi("SalaryCalculationSettings/rules", "GET", "", params.toString());
        if (response) {
            setSalaryRule(response || null);
        }
    }

    const loadBenfit = async () => {
        const response = await fetchApi("DropDown/benefitList", "GET", null, `branch_Id=${branchId}`);
        if (response) setBenfits(response);
    }

    const loadSalaryCalculationRuleWithoutPF = async () => {
        const response = await fetchApi("SalaryCalculationSettings/GetSalarySlabBranchWise", "GET", null, `branch_Id=${branchId}`)
        if (response) setCalculationRuleWithoutPF(response)
    }

    useEffect(() => {
        if (branchId && departmentId) {
            loadSalaryRule();
            loadBenfit();
        }
        if (branchId) loadSalaryCalculationRuleWithoutPF();
    }, [branchId, departmentId]);

    useEffect(() => {
        if (branchId && departmentId) {
            loadSalaryRule();
        }
    }, [employement_Type, intern_Payment_Type]);


    const onSubmit = async (data: SalaryForm) => {

        if (!employeeId) {
            addToast({ message: "Employee ID not found", status: "error" });
            return;
        }
        setLoading(true);
        const payload = {
            EmpId: employeeId,
            Gross_Salary: data.salary_Per_Month ?? 0,
            Basic_Salary: data.basic_salary ?? 0,
            Basic_Salary_For_PF: data.basic_Salary_for_pf ?? data.basic_salary ?? 0,
            includePF: includePF,
            IncludeESIC: includeESIC,
            EmployeePF: data.employeePF ?? 0,
            EmployerPF: data.employerPF ?? 0,
            Esic_Applicable: data.esicEmployee !== 0 ? true : false,
            EsicEmployee: data.esicEmployee ?? 0,
            EsicEmployer: data.esicEmployer ?? 0,
            Meal_Allowance_Applied: !!data.mealAllowance,
            Meal_Allowance_Amount: data.mealAllowance ? benfits?.meal_Allowance ?? 0 : 0,
            Transport_Allowance_Applied: !!data.transportAllowance,
            Transport_Allowance_Amount: data.transportAllowance ? benfits?.transportation_Allowance ?? 0 : 0,
            Health_Insurance_Applied: !!data.healthInsurance,
            Health_Insurance_Amount: data.healthInsurance ? benfits?.health_Insurance_Coverage ?? 0 : 0,
            MonthlyCTC: data.monthlyCTC ?? 0,
            AnnualCTC: data.annualCTC ?? 0,
            Net_Take_Home_Salary: data.netTakeHomeSalary ?? 0,
            Hra: data.hra ?? 0,
            Conveyance: data.conveyance ?? 0,
            employement_Type: data.employement_Type,
            intern_Payment_Type: data.intern_Payment_Type
        };
        setSalary(data);
        let endpoint = `Hrlense_Employee/empSalary/${employeeId}`;
        const response = await fetchApi(endpoint, "PUT", payload, null, "");
        if (response) {
            addToast({ message: response.message, status: 'success' });
            nextStep();
        }
        setLoading(false);
    }



    useEffect(() => {

        if (!grossMonthValue || !salaryRule || grossMonthValue <= salaryRule?.min_Gross_Salary) {
            setValue("basic_salary", 0);
            setValue("basic_Salary_for_pf", 0);
            setValue("hra", 0);
            setValue("conveyance", 0);

            setValue("employeePF", 0);
            setValue("employerPF", 0);
            setValue("esicEmployee", 0);
            setValue("esicEmployer", 0);

            setValue("monthlyCTC", 0);
            setValue("annualCTC", 0);
            setValue("netTakeHomeSalary", 0);

            setValue("healthInsurance", false);
            setValue("mealAllowance", false);
            setValue("transportAllowance", false);

            return;
        }

        const result =
            employement_Type === "INTERN"
                ? calculateInternSalary({ gross: grossMonthValue, rule: salaryRule, benefits: benfits, options: { meal: mealAllowance, transport: transportAllowance, health: healthInsurance, includePF, includeESIC }, })
                : calculateEmployeeSalary({ gross: grossMonthValue, rule: salaryRule, benefits: benfits, options: { meal: mealAllowance, transport: transportAllowance, health: healthInsurance, includePF, includeESIC }, withoutPfCalculationRule: calculationRuleWithoutPF, });

        if (!result) return;

        setValue("basic_salary", result.basic);
        setValue("basic_Salary_for_pf", result.pfBasic);
        setValue("hra", result.hra);
        setValue("conveyance", result.conveyance);

        setValue("employeePF", result.employeePF);
        setValue("employerPF", result.employerPF);
        setValue("esicEmployee", result.esicEmployee);
        setValue("esicEmployer", result.esicEmployer);

        setValue("monthlyCTC", result.monthlyCTC);
        setValue("annualCTC", Math.round(result.annualCTC));
        setValue("netTakeHomeSalary", result.netSalary);

        if (grossMonthValue <= salaryRule.esiC_Wage_Limit) {
            setValue("healthInsurance", false);
        }

    }, [grossMonthValue, salaryRule, mealAllowance, transportAllowance, healthInsurance, benfits, setValue, includePF, includeESIC]);


    useEffect(() => {
        if (!grossMonthValue || !salaryRule) return;
        if (grossMonthValue <= salaryRule.pf_Deduction_Threshold) {
            setIncludePF(true);
        }
    }, [grossMonthValue, salaryRule]);


    useEffect(() => {
        if (employement_Type === "INTERN" && intern_Payment_Type === "UNPAID") {
            setValue("basic_salary", 0);
            setValue("basic_Salary_for_pf", 0);
            setValue("hra", 0);
            setValue("conveyance", 0);

            setValue("employeePF", 0);
            setValue("employerPF", 0);
            setValue("esicEmployee", 0);
            setValue("esicEmployer", 0);

            setValue("monthlyCTC", 0);
            setValue("annualCTC", 0);
            setValue("netTakeHomeSalary", 0);
        }
    }, [employement_Type, intern_Payment_Type, setValue]);

    const pct = (value?: number) => value !== undefined ? `(${value}%)` : "";

    const isSalaryRuleMissing = !salaryRule;

    const handleGrossSalaryBlur = (gross: number) => {
        if (salaryRule && employement_Type === "EMPLOYEE" && gross > 0 && gross < Number(salaryRule.min_Gross_Salary)) {
            setAlertConfig({
                colorScheme: "orange",
                description: `The minimum gross Salary configured is ₹${salaryRule.min_Gross_Salary}.You entered Gross Salary ₹${gross}.Do you want to convert this employee to a Paid Intern?`,
                onConfirm: () => {
                    setValue("employement_Type", "INTERN");
                    setValue("intern_Payment_Type", "PAID");
                }
            });
        }
    };

    const handleInternAlertClose = () => {
        const gross = Number(watch("salary_Per_Month"));

        if (employement_Type === "EMPLOYEE" && gross > 0 && gross < Number(salaryRule?.min_Gross_Salary)) {
            setValue("salary_Per_Month", 0);

            setValue("basic_salary", 0);
            setValue("basic_Salary_for_pf", 0);
            setValue("hra", 0);
            setValue("conveyance", 0);

            setValue("employeePF", 0);
            setValue("employerPF", 0);
            setValue("esicEmployee", 0);
            setValue("esicEmployer", 0);

            setValue("monthlyCTC", 0);
            setValue("annualCTC", 0);
            setValue("netTakeHomeSalary", 0);
        }

        setAlertConfig(null);
    };

    return (
        <MyDiv>
            <Box m={6}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Text fontWeight="bold" fontSize="lg" mt={4} mb={2} color="blue.600">
                        Earning
                    </Text>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                        <FormControl>
                            <FormLabel>Employment Type</FormLabel>
                            <Controller name="employement_Type" control={control} render={({ field }) => (
                                <Select {...field}>
                                    <option value="EMPLOYEE">Employee</option>
                                    <option value="INTERN">Intern</option>
                                </Select>
                            )}
                            />
                        </FormControl>
                        {employement_Type === "INTERN" && (
                            <FormControl>
                                <FormLabel>Intern Type</FormLabel>
                                <Controller name="intern_Payment_Type" control={control} render={({ field }) => (
                                    <Select {...field}>
                                        <option value="">Select</option>
                                        <option value="PAID">Paid Intern</option>
                                        <option value="UNPAID">Unpaid Intern</option>
                                    </Select>
                                )}
                                />
                            </FormControl>
                        )}

                        <FormControl mb={4} isInvalid={!!errors.salary_Per_Month}>
                            <FormLabel>Gross Salary Per Month</FormLabel>
                            {/* <Controller name="salary_Per_Month" control={control} render={({ field }) => <Input type="number" {...field} isDisabled={isSalaryRuleMissing} />} /> */}
                            <Controller name="salary_Per_Month" control={control} render={({ field }) => (
                                <Input type="number" {...field} isDisabled={isSalaryRuleMissing} onBlur={(e) => {
                                    field.onBlur();
                                    handleGrossSalaryBlur(Number(e.target.value));
                                }}
                                />
                            )}
                            />
                            <FormErrorMessage> {errors.salary_Per_Month?.message} </FormErrorMessage>
                        </FormControl>
                        <HrlenseInput disable={isSalaryRuleMissing} name="basic_salary" label={`Basic Salary ${getPercentage(watch("basic_salary"))}`} control={control} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} name="basic_Salary_for_pf" label="Basic salary for PF" type="number" control={control} placeholder="Enter PF Basic" readOnly />

                        <Controller name="mealAllowance" control={control} render={({ field }) => (
                            <Checkbox disabled={!grossMonthValue} isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} isDisabled={field.disabled} >
                                Meal Allowance {benfits?.meal_Allowance ? ` ₹(${benfits.meal_Allowance})` : ''} </Checkbox>)} />
                        <Controller name="transportAllowance" control={control} render={({ field }) => (
                            <Checkbox disabled={!grossMonthValue} isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} >
                                Transport Allowance {benfits?.transportation_Allowance ? ` ₹(${benfits.transportation_Allowance})` : ''}</Checkbox>)}
                        />


                        {grossMonthValue && grossMonthValue > salaryRule?.esiC_Wage_Limit && (<Controller name="healthInsurance" control={control} render={({ field }) => (
                            <Checkbox disabled={!grossMonthValue} isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} >
                                Health Insurance {benfits?.health_Insurance_Coverage ? ` ₹(${benfits.health_Insurance_Coverage})` : ''}</Checkbox>)} />
                        )}

                        <Checkbox isChecked={includePF} onChange={(e) => setIncludePF(e.target.checked)} isDisabled={!grossMonthValue || isSalaryRuleMissing || grossMonthValue < salaryRule?.pf_Deduction_Threshold}>Include PF</Checkbox>

                        {salaryRule?.is_ESIC_Required && grossMonthValue > 0 && grossMonthValue <= salaryRule?.esiC_Wage_Limit && (
                            <Checkbox
                                isChecked={includeESIC}
                                onChange={(e) => setIncludeESIC(e.target.checked)}
                                isDisabled={!grossMonthValue || isSalaryRuleMissing}
                            >
                                Include ESIC
                            </Checkbox>
                        )}

                        <HrlenseInput disable={isSalaryRuleMissing} name="employeePF" label={`Employee PF (12%)`} control={control} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} name="employerPF" label={`Employer PF (12%)`} control={control} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} name="esicEmployee" label={`Employee ESIC ${getPercentage(watch("esicEmployee"))}`} control={control} readOnly />


                        <HrlenseInput disable={isSalaryRuleMissing} name="esicEmployer" label={`Employer ESIC  ${getPercentage(watch("esicEmployer"))}`} control={control} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} isRequired name="hra" label={`HRA ${getPercentage(watch("hra"))}`} control={control} errors={errors} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} isRequired name="conveyance" label={`Conveyance ${getPercentage(watch("conveyance"))}`} control={control} errors={errors} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} name="monthlyCTC" label="Monthly CTC" control={control} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} name="annualCTC" label="Annual CTC" control={control} readOnly />
                        <HrlenseInput disable={isSalaryRuleMissing} name="netTakeHomeSalary" label="Net Salary" control={control} readOnly />

                    </Grid>
                    <Box mt={6} display="flex" justifyContent="space-between">
                        <Button onClick={prevStep} colorScheme="gray" leftIcon={<ArrowBackIcon boxSize={4} />}>
                            Previous
                        </Button>
                        <Button type="submit" isLoading={loading} colorScheme="green" rightIcon={<ArrowForwardIcon boxSize={4} />}>
                            Save & Next
                        </Button>
                    </Box>
                </form>
            </Box>

            <AlertCard title="Minimum Salary Validation" isLoading={false} isOpen={!!alertConfig} onClose={() => handleInternAlertClose()} onConfirm={() => { if (alertConfig?.onConfirm) alertConfig.onConfirm(); setAlertConfig(null); }} description={alertConfig?.description || ''} colorScheme={alertConfig?.colorScheme || 'red'} />
        </MyDiv>
    );
};

export default SalaryCompensation;